import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/combo-select-input';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { modifier } from 'ember-modifier';
import type { SupportedCurrencies } from 'frontend-utils/utils/constants/currency-mapping-constants';
import type { Currency } from 'lumen/models';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

interface GeneralCurrencySelectInputSignature {
  Args: {
    isSecondary?: boolean;
    onCurrencyChange: (currencyOption: Option) => void;
    selectedCurrencyCode: SupportedCurrencies;
  };
}

export default class GeneralCurrencySelectInput extends Component<GeneralCurrencySelectInputSignature> {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intl: Services['intl'];
  @service declare store: Services['store'];

  fetchCurrencies = modifier(
    (_element, [currency]: [SupportedCurrencies]): void => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.fetchCurrenciesTask.perform(currency);
    },
  );

  fetchCurrenciesTask = task(
    { restartable: true },
    async (currency: SupportedCurrencies) => {
      const userId = this.hotelAndMarketService.user.id;

      try {
        const currencies = await this.store.query<Currency>('currency', {
          namespace: APIGATEWAY,
          typeKey: `users/${userId}/currencies`,
          reference_currency: currency,
        });

        return currencies.slice();
      } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert({
          text: getAPIErrorMessage(error, this.intl),
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currencies() {
    return this.fetchCurrenciesTask.last?.value ?? [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currencyOptions() {
    return this.currencies.map((currency) => ({
      id: currency.code,
      label: `${currency.name} (${currency.symbol})`,
      unsafe_subLabel: this.getCurrencySubLabel(currency),
    }));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedOption() {
    return this.currencyOptions.find(
      (option) => option.id === this.args.selectedCurrencyCode,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getCurrencySubLabel(currency: Currency) {
    if (currency.code === this.args.selectedCurrencyCode) {
      return undefined;
    }

    const roundedReferenceValue = this.intl.formatNumber(
      currency.referenceValue,
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: 5,
      },
    );

    return `1 ${this.args.selectedCurrencyCode} = ${roundedReferenceValue} ${currency.code}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  searchMatcher(option: Option & { id: string }, searchText: string) {
    const searchLower = searchText.toLowerCase();
    const matchesCurrency =
      option.label.toLowerCase().includes(searchLower) ||
      option.id.toLowerCase().includes(searchLower);
    return matchesCurrency ? 1 : -1;
  }

  <template>
    <div
      class="currency-select-input flex flex-col gap-100"
      {{this.fetchCurrencies @selectedCurrencyCode}}
    >
      <p class="text-200--bold">{{t "general.currency"}}</p>
      <PrismComboSelectInput
        @isSearchable={{true}}
        @isSecondary={{@isSecondary}}
        @onChange={{@onCurrencyChange}}
        @options={{this.currencyOptions}}
        @searchMatcher={{this.searchMatcher}}
        @unsafe_matchTriggerWidth={{true}}
        @value={{this.selectedOption}}
        data-test-field="Currency"
      />
    </div>
  </template>
}
